import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiscardChangesDialog } from '../../../shared/ui/feedback/DiscardChangesDialog/DiscardChangesDialog';
import type { CalendarOrderEditor } from '../model/useCalendarOrderEditor';

interface Props {
  editor: CalendarOrderEditor;
}

/** The internal page an anchor click would open, or `null` for anything that stays on this page. */
function targetPath(event: MouseEvent): string | null {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;

  const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return null;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return null;

  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Asks before the Leitung leaves the page with an order they have not saved.
 *
 * Three ways out are covered: the "Abbrechen" button (via `editor.requestCancel`), a click on any
 * link to another page, and closing or reloading the tab. The app runs on `BrowserRouter`, which has
 * no navigation blocker, so links are caught in the capture phase before React Router sees them;
 * the browser's Back button is not covered.
 *
 * Nothing is armed until something was actually changed — an edit mode that was only opened leaves
 * without a question.
 */
export const CalendarOrderLeaveGuard = ({ editor }: Props) => {
  const navigate = useNavigate();
  const { draft, guard } = editor;
  const dirty = draft !== null;

  useEffect(() => {
    if (!dirty) return;

    const onClick = (event: MouseEvent) => {
      const path = targetPath(event);
      if (!path) return;

      event.preventDefault();
      event.stopPropagation();
      guard(() => navigate(path));
    };

    // The browser's own prompt; the text is not customisable.
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    document.addEventListener('click', onClick, true);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [dirty, guard, navigate]);

  return (
    <DiscardChangesDialog
      open={editor.isConfirmingDiscard}
      onConfirm={editor.confirmDiscard}
      onCancel={editor.dismissDiscard}
      message="Die geänderte Reihenfolge der Handwerker ist noch nicht gespeichert und geht verloren, wenn Sie jetzt fortfahren."
    />
  );
};
